'use client';

import { AnimatePresence, motion } from 'motion/react';
import React, { startTransition, useDeferredValue, useEffect, useRef, useState } from 'react';

type ConrnerPosition = 'bottom-right' | 'bottom-left';

type MediaFrameProps = {
  isVisible: boolean;
  title: string;
  initialCornerPosition?: ConrnerPosition;
  children: React.ReactNode;
  onClose: () => void;
};

const CONSTANTS = {
  BAR_HEIGHT: 36,
  HEADER_HEIGHT: 36,
  MOBILE_BREAKPOINT: 768,
  DEFAULT_WIDTH: 480,
  MIN_WIDTH: 320,
  ASPECT_RATIO: 16 / 9,
  CORNER_PADDING: 20,
  ANIMATION_DURATION: 0.3,
} as const;

const containerVariants = {
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'tween',
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      type: 'tween',
      duration: 0.25,
      ease: 'easeIn'
    }
  }
};


const MediaFrame: React.FC<MediaFrameProps> = ({
  isVisible,
  title,
  initialCornerPosition = 'bottom-right',
  children,
  onClose,
}) => {

  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [animating, setAnimating] = useState<boolean>(false);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);

  const [hasErrorOccurred, setHasErrorOccurred] = useState<boolean>(false);

  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const deferredWindowWidth = useDeferredValue(windowWidth);

  const [cornerPosition, setCornerPosition] = useState<ConrnerPosition>(initialCornerPosition);

  const containerRef = useRef<HTMLDivElement>(null);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);

  const getDimensions = () => {
    let width = isMinimized
      ? CONSTANTS.MIN_WIDTH
      : Math.min(deferredWindowWidth - CONSTANTS.CORNER_PADDING * 2, CONSTANTS.DEFAULT_WIDTH);

    if (isMobile) {
      width = isMinimized
        ? CONSTANTS.MIN_WIDTH
        : Math.min(deferredWindowWidth - CONSTANTS.CORNER_PADDING * 2, CONSTANTS.DEFAULT_WIDTH);
    }

    const height = width / CONSTANTS.ASPECT_RATIO;

    return { width, height };
  };

  const { width: playerWidth, height: playerHeight } = getDimensions();

  const getContainerHeight = () => {
    if (isMobile && isHidden) return CONSTANTS.BAR_HEIGHT;
    return playerHeight + CONSTANTS.HEADER_HEIGHT;
  };

  const getPositionStyle = (): React.CSSProperties => {
    if (isMobile && isHidden) {
      return {
        top: 'auto',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
      };
    }

    if (isMobile) {
      return {
        top: 'auto',
        bottom: `${CONSTANTS.CORNER_PADDING / 2}px`,
        left: 0,
        right: 0,
        width: '100%',
        margin: '0 auto',
        maxWidth: `${playerWidth}px`,
      };
    }

    if (cornerPosition === 'bottom-right') {
      return {
        top: 'auto',
        bottom: `${CONSTANTS.CORNER_PADDING}px`,
        left: 'auto',
        right: `${CONSTANTS.CORNER_PADDING}px`,
        width: `${playerWidth}px`,
      };
    }

    return {
      top: 'auto',
      bottom: `${CONSTANTS.CORNER_PADDING}px`,
      left: `${CONSTANTS.CORNER_PADDING}px`,
      right: 'auto',
    };
  };

  const toggleCornerPosition = () => {
    if (animating) return;

    startTransition(() => {
      setAnimating(true);
      setCornerPosition(cornerPosition === 'bottom-right' ? 'bottom-left' : 'bottom-right');

      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);

      animationTimerRef.current = setTimeout(() => {
        setAnimating(false);
        animationTimerRef.current = null;
      }, CONSTANTS.ANIMATION_DURATION * 1000);
    });
  };

  const toggleMinimize = () => {
    startTransition(() => {
      setIsMinimized(!isMinimized);
      setIsHidden(false);
    });
  };

  const toggleHiddenState = () => {
    startTransition(() => {
      setIsHidden(!isHidden);
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const newWindowWidth = window.innerWidth;
      startTransition(() => {
        setWindowWidth(newWindowWidth);
        setIsMobile(newWindowWidth < CONSTANTS.MOBILE_BREAKPOINT);
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    };
  }, []);

  const getTransitionString = () => {
    return `all ${CONSTANTS.ANIMATION_DURATION}s cubic-bezier(0.25, 0.1, 0.25, 1.0)`;
  };

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    overflow: 'hidden',
    width: `${playerWidth}px`,
    height: `${getContainerHeight()}px`,
    transition: getTransitionString(),
    borderRadius: isMobile ? (isHidden ? '0' : '6px 6px 0 0') : '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    zIndex: 9999,
    ...getPositionStyle(),
  };

  const headerController = () => {
    
    const headerStyle: React.CSSProperties = {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      height: `${CONSTANTS.HEADER_HEIGHT}px`,
      backgroundColor: 'rgba(0, 0, 0, 0.1)',
      color: 'white',
      userSelect: 'none'
    };

    const titleStyle: React.CSSProperties = {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    };

    const buttonStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 4px',
      padding: '4px',
      cursor: 'pointer',
      color: 'white',
      border: 'none',
      background: 'none',
      touchAction: 'manipulation',
      transition: 'opacity 0.2s ease',
    };
    
    return (
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {hasErrorOccurred && (<>ERROR</>)}

          <div style={titleStyle}>
            <p>{title}</p>
          </div>

          <div style={{ display: 'flex' }}>
            {isMobile && (
              <button
                style={buttonStyle}
                onClick={toggleHiddenState}
                title='Toggle visibility'
              >
                {isHidden ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </button>
            )}

            {!isMobile && (
              <button
                style={{
                  ...buttonStyle,
                  opacity: animating ? 0.5 : 1,
                  cursor: animating ? 'default' : 'pointer',
                }}
                onClick={toggleCornerPosition}
                disabled={animating}
                title='Toggle corner position'
              >
                {cornerPosition === 'bottom-left' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 10 4 15 9 20" />
                    <path d="M20 4v7a4 4 0 0 1-4 4H4" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 10 20 15 15 20" />
                    <path d="M4 4v7a4 4 0 0 0 4 4h12" />
                  </svg>
                )}
              </button>
            )}

            {((isMobile && !isHidden) || !isMobile) && (
              <button
                style={buttonStyle}
                onClick={toggleMinimize}
                title={isMinimized ? 'Maximize' : 'Minimize'}
              >
                {isMinimized ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h6v6" />
                    <path d="M9 21H3v-6" />
                    <path d="M21 3l-7 7" />
                    <path d="M3 21l7-7" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 14h6v6" />
                    <path d="M20 10h-6V4" />
                    <path d="M14 10l7-7" />
                    <path d="M3 21l7-7" />
                  </svg>
                )}
              </button>
            )}
            
            <button
              style={buttonStyle}
              onClick={onClose}
              title='Close'
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </header>
    );
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={containerRef}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={containerVariants}
          style={containerStyle}
        >
          <div style={{ display: 'block', width: '100%', height: '100%' }}>
            {headerController()}
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

};

export default MediaFrame;